import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/common/useAuth'
import { getCurrentUserSpaces } from '@/api/requests/space'
import { waitForAuth, isAuthenticated } from '@/utils/auth-helpers'
import { logger } from '@/utils/logger'

function useSpaceRedirect() {
  const { user, loading, isSigningUp } = useAuth()
  const [initialSpace, setInitialSpace] = useState<string | null>(null)
  const [checkingSpace, setCheckingSpace] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    const checkUserSpace = async () => {
      logger.debug('スペースリダイレクトチェック開始', {
        userId: user?.id,
        userEmail: user?.email,
        authLoading: loading,
        isSigningUp,
        retryCount,
      })

      // サインアップ中はスペースチェックをスキップ
      if (isSigningUp) {
        logger.debug('サインアップ中のため、スペースチェックをスキップ')
        setCheckingSpace(false)
        return
      }

      if (!user?.id) {
        logger.debug('ユーザーIDなし、チェック終了')
        setCheckingSpace(false)
        return
      }

      try {
        setAuthError(null) // エラー状態をリセット

        // 認証状態を待機（最大3秒）
        logger.debug('認証状態の準備を待機中...')
        const authReady = await waitForAuth(3000)
        if (!authReady || !isAuthenticated()) {
          const errorMsg = 'Authentication not ready - user may need to log in again'
          logger.error('認証状態の準備が完了していません', {
            authReady,
            isAuthenticated: isAuthenticated(),
            userEmail: user.email,
          })
          setAuthError(errorMsg)
          setCheckingSpace(false)
          return
        }

        logger.debug('認証状態確認完了、スペース取得開始')
        const spaces = await getCurrentUserSpaces()

        logger.debug('スペース取得結果', {
          spacesCount: spaces?.length || 0,
          firstSpaceId: spaces?.[0]?.id,
          allSpaceIds: spaces?.map(s => s.id) || [],
        })

        if (spaces && spaces.length > 0) {
          setInitialSpace(spaces[0].id || null)
          logger.info('初期スペース設定完了', { spaceId: spaces[0].id })
        } else {
          logger.info('ユーザーのスペースが見つかりません')
          setInitialSpace(null)
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        logger.error('スペースチェックエラー:', {
          error: errorMessage,
          userEmail: user.email,
          userId: user.id,
          isAuthenticated: isAuthenticated(),
          retryCount,
        })
        setAuthError(errorMessage)
        setInitialSpace(null)

        // 認証エラーの場合、自動リトライを試行（最大2回）
        if (errorMessage.includes('Authentication') || errorMessage.includes('authorization')) {
          if (retryCount < 2) {
            logger.info('認証エラーのため自動リトライを実行', {
              retryCount: retryCount + 1,
              maxRetries: 2,
            })
            setRetryCount(prev => prev + 1)
            setTimeout(
              () => {
                setCheckingSpace(true)
              },
              1000 * (retryCount + 1)
            ) // 徐々に遅延を増加
            return
          } else {
            logger.error('自動リトライ上限に達しました', { retryCount })
          }
        }
      } finally {
        setCheckingSpace(false)
      }
    }

    if (!loading && !isSigningUp) {
      checkUserSpace()
    }
  }, [user, loading, isSigningUp, retryCount])

  // 手動リトライ機能
  const manualRetry = () => {
    logger.debug('手動リトライ実行', { retryCount })
    setCheckingSpace(true)
    setAuthError(null)
    setRetryCount(0) // 手動リトライの場合はカウンターをリセット
  }

  return {
    initialSpace,
    isLoading: loading || checkingSpace,
    authError,
    retryCount,
    manualRetry,
  }
}

export { useSpaceRedirect }
